import { useState, useEffect } from 'react';
import { useAppKitAccount } from '@reown/appkit/react';
import './config/appkit'; // Ensure AppKit is initialized before hooks run
import { useAccount } from 'wagmi';
// Solana wallet support removed - EVM chains only
import './App.css';
import { API_CONFIG } from './config/constants';

// AppKit account will be used directly in components

// Import components
import PaymentWidget from './components/PaymentWidget';
import UserDashboard from './components/UserDashboard';
import PresaleInfo from './components/PresaleInfo';
import WalletConnect from './components/WalletConnect';
import ErrorBoundary from './components/ErrorBoundary';

interface PresaleInfo {
  name: string;
  isActive: boolean;
  currentPhase: number;
  priceUsd: number;
  tokensAvailable: number;
  tokensSold: number;
  totalUsdRaised: number;
  timeRemaining: number;
  phaseEndTime: string;
}

function App() {
  // EVM wallet connection methods only
  const { address: appKitAddress, isConnected: appKitConnected } = useAppKitAccount();
  const { address: evmAddress, isConnected: evmConnected } = useAccount();
  
  // Determine current connection state - EVM only
  const isConnected = evmConnected || appKitConnected;
  const address = evmAddress || appKitAddress;
  
  const [presaleInfo, setPresaleInfo] = useState<PresaleInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [networkWarning, setNetworkWarning] = useState<string | null>(null);
  // const [connectedNetwork, setConnectedNetwork] = useState<string>(''); // Unused for now

  // Fetch presale info on mount and poll every 10 seconds
  useEffect(() => {
    fetchPresaleInfo();
    const interval = setInterval(fetchPresaleInfo, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchPresaleInfo = async () => {
    try {
      setError(null);
      
      console.log('Fetching presale info from:', `${API_CONFIG.BASE_URL}/api/presale/info`);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/presale/info`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch presale info');
      }
      
      const data = await response.json();
      setPresaleInfo(data);
    } catch (err) {
      console.error('Error fetching presale info:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    // Refresh presale info after successful payment
    fetchPresaleInfo();
  };

  const handleWalletConnected = (walletAddress: string, network: string) => {
    // setConnectedNetwork(network); // Disabled for now
    console.log(`Wallet connected: ${walletAddress} on ${network}`);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Loading presale information...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">🚀 Token Presale</h1>
        <WalletConnect 
          onNetworkWarning={setNetworkWarning}
          onWalletConnected={handleWalletConnected}
        />
      </header>

      <main className="app-main">
        {error && (
          <div className="error-message">
            <p>⚠️ {error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {networkWarning && (
          <div className="network-warning">
            <p>⚠️ {networkWarning}</p>
          </div>
        )}

        {presaleInfo && (
          <PresaleInfo 
            presaleInfo={presaleInfo}
            onRefresh={fetchPresaleInfo}
          />
        )}

        {isConnected && address && presaleInfo ? (
          <div className="user-section">
            <ErrorBoundary>
              <PaymentWidget 
                walletAddress={address}
                pricePerToken={presaleInfo.priceUsd}
                currentPhase={presaleInfo.currentPhase}
                isActive={presaleInfo.isActive}
                onSuccess={handlePaymentSuccess}
              />
            </ErrorBoundary>
            
            <ErrorBoundary>
              <UserDashboard 
                walletAddress={address}
              />
            </ErrorBoundary>
          </div>
        ) : (
          <div className="connect-prompt">
            <div className="connect-prompt-content">
              <h2>Connect Your Wallet</h2>
              <p>Connect your wallet to participate in the presale and track your investments</p>
              <div className="connect-features">
                <div className="feature">
                  <span className="feature-icon">💰</span>
                  <span>Purchase tokens with crypto</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📊</span>
                  <span>Track your investments</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🔒</span>
                  <span>Secure transactions</span>
                </div>
              </div>
              <div className="supported-wallets">
                <p>Supported wallets:</p>
                <div className="wallet-types">
                  <span>MetaMask</span>
                  <span>WalletConnect</span>
                  <span>Coinbase Wallet</span>
                  <span>Phantom</span>
                  <span>Solflare</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>© 2024 Token Presale. Powered by Radom & Cloudflare.</p>
      </footer>
    </div>
  );
}

export default App;
